import { useNavigate } from '@tanstack/react-router';
import { useTimezone } from '@/components/contexts/TimezoneContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/NewButton';
import { FEATURE_FLAGS, useFeatures } from '@/hooks/useFeatures';
import type { HomeResumeItem } from '@/hooks/useHomeDashboard';
import { useHomeResumeItems } from '@/hooks/useHomeResumeItems';
import { DeadlineWidgetStatus } from '@/pages/home/deadline-widgets/shared';
import { ResumeItemRow } from '@/pages/home/ResumeItemRow';

export function ResumePanel() {
  const { timezone } = useTimezone();
  const navigate = useNavigate();
  const { isEnabled: draftingEnabled } = useFeatures(FEATURE_FLAGS.DRAFTING);
  const {
    items,
    hasMore,
    isLoading,
    isLoadingMore,
    isError,
    loadMore,
    retry,
  } = useHomeResumeItems({ timezone, includeDrafts: draftingEnabled, enabled: true });

  const visibleItems = draftingEnabled
    ? items
    : items.filter((item) => item.kind === 'conversation');

  const openItem = (item: HomeResumeItem) => {
    if (item.kind === 'conversation') {
      navigate({
        to: '/matter/$slug/case-partner/$chatGuid',
        params: { slug: item.matter_guid, chatGuid: item.item_guid },
      });
      return;
    }
    navigate({
      to: '/matter/$slug/drafting/$documentGuid',
      params: { slug: item.matter_guid, documentGuid: item.item_guid },
    });
  };

  const subtitle = draftingEnabled
    ? 'Your conversations and drafts from the past month'
    : 'Your conversations from the past month';
  const emptyMessage = draftingEnabled
    ? 'Conversations and drafts you start will appear here.'
    : 'Conversations you start will appear here.';
  const errorMessage = draftingEnabled
    ? "Couldn't load conversations and drafts."
    : "Couldn't load conversations.";

  return (
    <Card flat className="flex h-full min-h-0 w-full flex-col px-6">
      <div className="mb-3 py-4">
        <h2 className="text-sm font-semibold tracking-tight text-slate-700">
          Conversations
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
      </div>
      <DeadlineWidgetStatus
        isLoading={isLoading}
        isError={Boolean(isError)}
        onRetry={retry}
        message={errorMessage}
      >
        {visibleItems.length === 0 && !hasMore ? (
          <p className="py-10 text-center text-sm text-muted-foreground">
            {emptyMessage}
          </p>
        ) : (
          <>
            {visibleItems.length > 0 ? (
              <ul className="max-h-[min(70vh,40rem)] divide-y divide-slate-100/80 overflow-y-auto">
                {visibleItems.map((item) => (
                  <ResumeItemRow
                    key={`${item.kind}-${item.item_guid}`}
                    item={item}
                    onOpen={openItem}
                  />
                ))}
              </ul>
            ) : null}
            {hasMore ? (
              <div className="flex justify-center pt-4">
                <Button
                  text="Load more"
                  variant="outline"
                  size="sm"
                  loading={isLoadingMore}
                  onClick={() => void loadMore()}
                />
              </div>
            ) : null}
          </>
        )}
      </DeadlineWidgetStatus>
    </Card>
  );
}
